import { decimalMinutesToFormattedTime } from '../../../shared/chart-options';

export function getGameDuration(stats) {
  const game = getGameData(stats);
  const league = game.stage.league;

  const gameDurationMinutes = league.num_periods * league.period_minutes;
  const overtimeMinutes = game.overtime ? league.overtime_minutes : 0;

  return {
    gameDurationMinutes,
    overtimeMinutes,
  };
}

/**
 * gameSeconds is the moment in the game we want to translate to 'normal' time
 * since it's a countdown to the end of the game, excluding the overtime (OT).
 *
 * For example:
 *   A 2 part game, 20 minutes each part -> has a duration of 40 minutes.
 *    It could have an OT of 5 minutes.
 *
 * The time of the game is always counted using the 'regular' duration without OT.
 *
 * In this example, a number of 2400 (2 parts * 20 minutes * 60 seconds) is translated to
 * minute 0 of the game. A number of 0 is translated to minute 40 of the game.
 *
 * If the game has OT the number of seconds will be negative.
 * -60 is translated as minute 1 of the OT period
 *
 * We need to performn this translation to locate in the scorechart timed scores.
 *
 *
 * @param {*} gameSeconds
 * @param {*} gameDurationSeconds
 */
export function getGameMinutes(gameSeconds, gameDurationSeconds) {
  let currentSeconds = gameDurationSeconds - gameSeconds;

  return currentSeconds / 60;
}

export function getAllPlayers(stats) {
  const game = getGameData(stats);

  return [...game.home.players, ...game.visitor.players];
}

export function getGameData(stats) {
  return stats.query.find(query => query.type.toUpperCase() === 'GAME').data;
}

export function getLineupsWithPlayers(lineups, allPlayers) {
  const lineupIdKeys = ['one', 'two', 'three', 'four', 'five'];

  return lineups.lineups.map(lineup => {
    const players = lineupIdKeys.map(key =>
      allPlayers.find(player => player.id === lineup.lineup[key]),
    );

    return { ...lineup, players };
  });
}

export function getShifts(lineups) {
  return lineups.reduce((shifts, lineup) => {
    const lineupShifts = lineup.shifts.map(shift => ({ ...shift, lineup }));

    return shifts.concat(lineupShifts);
  }, []);
}

export function getShiftStats(shift, gameDurationSeconds) {
  return {
    shift: getLineupShiftStats(shift, gameDurationSeconds),
    game: getLineupTotalStats(shift),
  };
}

function getLineupShiftStats(teamShift, gameDurationSeconds) {
  const time = decimalMinutesToFormattedTime(teamShift.boxscore.minutes);
  const startTime = decimalMinutesToFormattedTime(
    getGameMinutes(teamShift.start_game_time_lig, gameDurationSeconds),
  );
  const endTime = decimalMinutesToFormattedTime(
    getGameMinutes(teamShift.end_game_time_lig, gameDurationSeconds),
  );
  const possessions =
    (teamShift.calculated.possessions + teamShift.opp_calculated.possessions) /
    2;

  const minutes = teamShift.boxscore.minutes;
  const homePoints = teamShift.boxscore.pts;
  const homePointsPerMinute = minutes
    ? homePoints / teamShift.boxscore.minutes
    : 0;
  const homePointsPerPossession = possessions ? homePoints / possessions : 0;
  const homePlusMinus = teamShift.boxscore.plus_minus;

  const visitorPoints = homePoints - teamShift.boxscore.plus_minus;
  const visitorPointsPerMinute = minutes
    ? visitorPoints / teamShift.boxscore.minutes
    : 0;
  const visitorPointsPerPossession = possessions
    ? visitorPoints / possessions
    : 0;
  const visitorPlusMinus = teamShift.boxscore.plus_minus * -1;

  return {
    startTime,
    endTime,
    time,
    home: {
      points: homePoints,
      pointsPerMinute: homePointsPerMinute,
      pointsPerPossession: homePointsPerPossession,
      possessions: possessions,
      plusMinus: homePlusMinus,
    },
    visitor: {
      points: visitorPoints,
      pointsPerMinute: visitorPointsPerMinute,
      pointsPerPossession: visitorPointsPerPossession,
      possessions: possessions,
      plusMinus: visitorPlusMinus,
    },
  };
}

export function getLineupTotalStats(teamShift) {
  const time = decimalMinutesToFormattedTime(
    teamShift.lineup.total.boxscore.minutes,
  );

  const minutes = teamShift.lineup.total.boxscore.minutes;
  const homePossessions = teamShift.lineup.calculated.possessions;
  const homePoints = teamShift.lineup.total.boxscore.pts;
  const homePointsPerMinute = minutes ? homePoints / minutes : 0;
  const homePointsPerPossession =
    teamShift.lineup.calculated.pace.points_per_possession;
  const homePlusMinus = teamShift.lineup.total.boxscore.plus_minus;

  const visitorPossessions = teamShift.lineup.opp_calculated.possessions;
  const visitorPoints = teamShift.lineup.total.opp_boxscore.pts;
  const visitorPointsPerMinute = minutes ? visitorPoints / minutes : 0;
  const visitorPointsPerPossession =
    teamShift.lineup.opp_calculated.pace.points_per_possession;
  const visitorPlusMinus = teamShift.lineup.total.opp_boxscore.plus_minus;

  return {
    time,
    home: {
      points: homePoints,
      pointsPerMinute: homePointsPerMinute,
      pointsPerPossession: homePointsPerPossession,
      possessions: homePossessions,
      plusMinus: homePlusMinus,
    },
    visitor: {
      points: visitorPoints,
      pointsPerMinute: visitorPointsPerMinute,
      pointsPerPossession: visitorPointsPerPossession,
      possessions: visitorPossessions,
      plusMinus: visitorPlusMinus,
    },
  };
}
